import { useEffect, useId, useRef, useState } from "react";
import { TextInput } from "../../components/TextInput";
import { Button } from "../../components/Button";
import { SelectPopover } from "../../components/SelectPopover";
import { ModalCloseButton } from "../../components/ModalCloseButton";
import { cycleTabFocus } from "../../utils/focus-trap";
import { ROLE_COACH, ROLE_ORG_ADMIN } from "./adminRoles";
import "./AddUserModal.css";

export interface AddUserSubmission {
  email: string;
  firstName?: string;
  lastName?: string;
  role: string;
}

/** One role the dialog can offer, with the line that explains what it grants. */
export interface AddUserRoleOption {
  key: string;
  label: string;
  description: string;
}

/**
 * The two roles an organization can hold, for callers with nothing better.
 *
 * An organization's own settings screen passes the API's `assignable-roles` instead, so the copy a
 * coach reads comes from one place rather than being written twice.
 */
const DEFAULT_ROLES: AddUserRoleOption[] = [
  {
    key: "user",
    label: ROLE_COACH,
    description: "Sees the pages an organization admin gives them access to.",
  },
  {
    key: "admin",
    label: ROLE_ORG_ADMIN,
    description:
      "Full access to every page, and manages staff access and roles.",
  },
];

interface AddUserModalProps {
  isOpen: boolean;
  /** Names the organization in the default description. Ignored when `description` is given. */
  organizationName?: string;
  /** Replaces the line under the title, for a dialog that does something other than add directly. */
  description?: string;
  /** Defaults to "Add a user". */
  title?: string;
  /** Defaults to "Add user". */
  submitLabel?: string;
  /** Shown on the submit button while `busy`. Defaults to "Adding…". */
  busyLabel?: string;
  /** Defaults to coach and organization admin. */
  roles?: AddUserRoleOption[];
  /**
   * Whether to ask for a first and last name. Defaults to true.
   *
   * **False on the invitation path, because the API has nowhere to put them.**
   * `CreateInvitationRequest` carries only an email and a role; names sent with an invitation are
   * dropped by model binding and never stored, so an admin who typed one watched it vanish with no
   * error and no record. Nothing is lost by not asking: the invitee supplies their own names when
   * they accept, which is the only place those names have ever come from.
   *
   * Direct user creation keeps them — `CreateUserRequest` genuinely does take FirstName/LastName,
   * and platform staff creating an account outright are the only ones who can set them.
   */
  collectNames?: boolean;
  onClose: () => void;
  onSubmit: (user: AddUserSubmission) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}

/**
 * Adds someone to an organization, or invites them to join one.
 *
 * Replaces a row of unlabelled placeholder-only inputs that expanded under the Users heading. The
 * placeholders were the only labels, so they vanished the moment anyone typed, and the role sat at
 * the end of the row where it read as another text field.
 *
 * Fields are labelled and stacked, and the role carries a line explaining what it grants — that
 * choice is the difference between a coach and someone who can manage the whole organization, and it
 * was the field most likely to be skipped.
 *
 * **Two callers, one dialog.** Platform staff create an account outright; an organization admin
 * sends an invitation. The fields, the labelling and the focus handling are the same either way, so
 * the copy and the role list are props rather than a second dialog that would drift from this one.
 */
export const AddUserModal = ({
  isOpen,
  organizationName,
  description,
  title = "Add a user",
  submitLabel = "Add user",
  busyLabel = "Adding…",
  roles = DEFAULT_ROLES,
  collectNames = true,
  onClose,
  onSubmit,
  busy = false,
  error,
}: AddUserModalProps) => {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  /*
    Null means "not chosen yet" and renders as the default below, rather than being resolved into
    state on open. The role list is a prop, so a parent that rebuilds the array on every render
    would otherwise have to be a dependency of the reset effect — and that effect clearing the
    email field mid-typing is exactly the bug that costs someone their input.
  */
  const [chosenRole, setChosenRole] = useState<string | null>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const roleId = useId();

  // Least privilege by default, whatever order the roles arrive in.
  const defaultRoleKey =
    roles.find((r) => r.key === "user")?.key ?? roles[0]?.key ?? "user";
  const role = chosenRole ?? defaultRoleKey;
  const selected = roles.find((r) => r.key === role);

  // Reset on open, not on close: clearing while it animates away shows the fields emptying.
  useEffect(() => {
    if (isOpen) {
      setEmail("");
      setFirstName("");
      setLastName("");
      setChosenRole(null);
    }
  }, [isOpen]);

  /*
    Focus moves into the dialog on open, done here rather than with `autoFocus`. The attribute fires
    on mount and never again, so reopening the dialog after the first time would leave focus behind
    on the Add user button while the dialog reads as the active surface.

    And it goes back where it came from on close. The organization settings screen closes this
    dialog for you once an invitation is sent, so without the restore a keyboard user is dropped on
    <body> at exactly the moment the new notice and the new table row are what they need to reach.
  */
  useEffect(() => {
    if (!isOpen) return;

    const previouslyFocused = document.activeElement;
    emailRef.current?.focus();

    return () => {
      if (previouslyFocused instanceof HTMLElement && previouslyFocused.isConnected) {
        previouslyFocused.focus();
      }
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      // Dismissal stays live while busy: a request that never settles would otherwise trap the
      // user here with no way out but a reload.
      if (e.key === "Escape") {
        onClose();
        return;
      }

      // aria-modal says the rest of the page is inert, so Tab has to be made to agree with it.
      // Same helper ConfirmModal uses, for the same reason.
      if (e.key !== "Tab") return;
      const dialog = dialogRef.current;
      if (dialog) cycleTabFocus(dialog, e);
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  /*
    The one gate on submitting. Both halves matter — an empty address has nothing to send, and a
    second submit while one is in flight would create the person twice — but only the second is
    enforced by this check alone, since the button stays pressable while busy. See the comment on it.
  */
  const canSubmit = email.trim().length > 0 && !busy;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    void onSubmit({
      email: email.trim(),
      firstName: firstName.trim() || undefined,
      lastName: lastName.trim() || undefined,
      role,
    });
  };

  return (
    <div className="add-user-overlay">
      {/*
        Click-outside-to-dismiss, as a sibling of the dialog rather than a handler on the overlay
        div wrapping it. That arrangement needs a stopPropagation on the dialog to stop its own
        clicks closing it, and the div carries a click handler with no keyboard path.

        Hidden from assistive technology and out of the tab order on purpose. It is a redundant
        mouse convenience — Escape, Cancel and the × are the real exits, and all three are reachable
        — so announcing a second unlabelled "Close" beside the one in the header only adds noise.
      */}
      <button
        type="button"
        className="add-user-overlay__scrim"
        data-testid="add-user-scrim"
        aria-hidden="true"
        tabIndex={-1}
        onClick={onClose}
      />
      <div
        className="add-user-modal"
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-user-title"
      >
        <div className="add-user-modal__header">
          <div>
            <h2 className="add-user-modal__title" id="add-user-title">
              {title}
            </h2>
            <p className="add-user-modal__subtitle">
              {description ??
                (organizationName
                  ? `They will be added to ${organizationName} and given a temporary password to share.`
                  : "They will be added to your organization and given a temporary password to share.")}
            </p>
          </div>
          <ModalCloseButton onClick={onClose} />
        </div>

        <form className="add-user-modal__body" onSubmit={handleSubmit}>
          <TextInput
            ref={emailRef}
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />

          {collectNames && (
            <div className="add-user-modal__row">
              <TextInput
                label="First name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
              <TextInput
                label="Last name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>
          )}

          <div className="add-user-modal__field">
            <label className="add-user-modal__label" htmlFor={roleId}>
              Role
            </label>
            <SelectPopover
              id={roleId}
              options={roles.map((r) => r.label)}
              value={selected?.label ?? ""}
              onChange={(label) =>
                setChosenRole(
                  roles.find((r) => r.label === label)?.key ?? defaultRoleKey
                )
              }
              placeholder={roles[0]?.label ?? ""}
              clearable={false}
              fullWidth
            />
            <span className="add-user-modal__hint">{selected?.description}</span>
          </div>

          {error && (
            <div className="add-user-modal__error" role="alert">
              {error}
            </div>
          )}

          <div className="add-user-modal__actions">
            <Button variant="secondary" type="button" onClick={onClose}>
              Cancel
            </Button>
            {/*
              Two ways to be unavailable, carried by two different attributes on purpose.

              In flight gets `aria-disabled`: this is the control the admin just activated, and a
              disabled button leaves the tab order on the spot — so the submit that sets `busy`
              blurs it and drops focus to <body>, outside an `aria-modal` dialog, where a refusal
              arriving a moment later is nowhere near where they are. `handleSubmit` refuses the
              second submit instead, so nothing is sent twice.

              An empty address keeps the native `disabled`. That button has not been activated, so
              there is no focus to lose, and `aria-disabled` there would leave a control that can be
              pressed, does nothing, and says nothing about why.
            */}
            <Button
              variant="primary"
              type="submit"
              disabled={email.trim().length === 0}
              aria-disabled={busy}
              aria-busy={busy}
            >
              {busy ? busyLabel : submitLabel}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
